// File: /js/shared/components/pushOptIn.js
//
// Web-push opt-in (App P1b, 2026-07-05).
//
// Browsers only show the notification-permission prompt after an explicit
// user gesture, so a proper-app-style "ask in the OS settings" flow is not
// available to the web platform (it IS what app-plan P3's native APNs will
// do). The app-like compromise, per AD (2026-07-05):
//
//   1. Running as the INSTALLED app (standalone display-mode): a one-time
//      bottom card on launch offers "Activer les notifications" — the way
//      a native app asks on first open. Dismissal is permanent
//      (localStorage). On iOS this is also the only context where web push
//      exists at all (Safari tabs can't subscribe — install first).
//   2. In a plain browser: a discreet control on the profile page
//      ("Notifications" tab, server-rendered by ProfilPage) — same
//      `data-ui` handlers, no nagging.
//
// All wiring registers on the existing `data-ui` registry (§1.3 rule-9
// carve-out: reuse, no new `document` seam); the save POST rides
// `sendFetchRequest` (CSRF auto-attached) to the audited
// `savePushSubscription` action. Feature-dark without a VAPID key in
// `window.bruits` (JsConfig emits it only when the env is configured).

import { i18n, pushVapidPublicKey } from '/js/shared/utils/config.js';
import { registerUiHandler } from '/js/shared/extensions/domExtensions.js';
import { sendFetchRequest } from '/js/shared/utils/AjaxUtils.js';

const DISMISS_KEY = 'lbdd.push.card_dismissed';
const CARD_ID = 'push-optin-card';
const SHOW_DELAY_MS = 2000;

function pushSupported() {
  return 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
    && pushVapidPublicKey() !== '';
}

function isStandalone() {
  return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    || window.navigator.standalone === true;
}

function isIosDevice() {
  const ua = navigator.userAgent;
  if (/iphone|ipod|ipad/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function isCardDismissed() {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; }
  catch { return false; }
}

function dismissCard() {
  try { localStorage.setItem(DISMISS_KEY, '1'); }
  catch { /* private mode — the card simply reappears next launch */ }
  const card = document.getElementById(CARD_ID);
  if (card) card.remove();
}

// The PushManager wants the VAPID key as a Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

async function currentSubscription() {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

async function subscribeAndSave() {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription()
    || await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(pushVapidPublicKey()),
    });

  const json = subscription.toJSON();
  return new Promise((resolve) => {
    sendFetchRequest({
      payload: {
        name: 'savePushSubscription',
        endpoint: subscription.endpoint,
        p256dh: (json.keys && json.keys.p256dh) || '',
        auth: (json.keys && json.keys.auth) || '',
      },
      onSuccess: () => resolve('enabled'),
      onError: () => resolve('error'),
    });
  });
}

/** Reflect the current state into the profile-tab control, if present. */
async function syncProfileControl() {
  const container = document.querySelector('[data-push-optin]');
  if (!container) return;

  const statusEl = container.querySelector('[data-push-status]');
  const buttonWrap = container.querySelector('[data-push-enable-wrap]');
  const setStatus = (key) => { if (statusEl) statusEl.textContent = i18n(key); };
  const hideButton = () => { if (buttonWrap) buttonWrap.classList.add('invisible'); };

  if (!pushSupported()) {
    // iOS Safari tab: push only exists once installed — say so instead of
    // a generic "unsupported".
    setStatus(isIosDevice() && !isStandalone()
      ? 'push.optin.status.ios_install'
      : 'push.optin.status.unsupported');
    hideButton();
    return;
  }
  if (Notification.permission === 'denied') {
    setStatus('push.optin.status.denied');
    hideButton();
    return;
  }
  if (Notification.permission === 'granted' && await currentSubscription()) {
    setStatus('push.optin.status.enabled');
    hideButton();
  }
}

registerUiHandler('click', 'push-enable', async () => {
  const state = await subscribeAndSave();
  if (state === 'enabled') dismissCard();
  const statusEl = document.querySelector('[data-push-optin] [data-push-status]');
  if (statusEl) statusEl.textContent = i18n(`push.optin.status.${state === 'error' ? 'error' : state}`);
  if (state === 'enabled') syncProfileControl();
});

registerUiHandler('click', 'push-card-dismiss', dismissCard);

function buildCard() {
  const card = document.createElement('div');
  card.id = CARD_ID;
  card.className = 'push-optin';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-label', i18n('push.optin.card_title'));
  card.innerHTML =
    '<div class="push-optin__card">'
    + '<p class="push-optin__title">' + i18n('push.optin.card_title') + '</p>'
    + '<p class="push-optin__body">' + i18n('push.optin.card_body') + '</p>'
    + '<div class="push-optin__actions">'
    + '<button type="button" class="push-optin__enable" data-ui="push-enable">' + i18n('push.optin.enable') + '</button>'
    + '<button type="button" class="push-optin__later" data-ui="push-card-dismiss">' + i18n('push.optin.later') + '</button>'
    + '</div>'
    + '</div>';
  return card;
}

export function initPushOptIn() {
  // Profile-tab control state (any context, even unsupported — it explains
  // itself there instead of silently hiding).
  syncProfileControl();

  // First-launch card: installed-app context only, once, and only while
  // the permission decision is still open.
  if (!isStandalone() || !pushSupported() || isCardDismissed()) return;
  if (Notification.permission !== 'default') return;
  setTimeout(() => {
    if (document.getElementById(CARD_ID)) return;
    document.body.appendChild(buildCard());
  }, SHOW_DELAY_MS);
}
